import { observer, Provider } from "mobx-react";
import { reaction } from "mobx";
import React, { Component } from "react";
import { HashRouter, Route, Switch, Redirect } from "react-router-dom";
import AppStore, { StoreProvider } from "./AppStore";
import authStore from "./authStore";
import config from "./config";
import Login from "./Login/Login";
import className from "./shared/className";
import Header from "./shared/Header/Header";
import "./shared/pageTransitions.scss";
import Documents from "./Widgets/Documents/Documents";
import ECG from "./Widgets/ECG/ECG";
import Images from "./Widgets/Images";
import LabDetail from "./Widgets/LabDetail";
import Labs from "./Widgets/Labs/Labs";
import MedDetail from "./Widgets/MedDetail";
import Meds from "./Widgets/Meds/Meds";
import MessagingContainer from "./Widgets/MessagingContainer";
import CreateStatement from "./Widgets/Monitor/CreateStatement";
import Monitor from "./Widgets/Monitor/Monitor";
import MonitorEvents from "./Widgets/Monitor/MonitorEvents";
import Tools from "./Widgets/Monitor/Tools";
import Patients from "./Widgets/Patients/Patients";
import PatientTiles from "./Widgets/PatientTiles/PatientTiles";
import ShareLink from "./Widgets/ShareLink";
import Surveil from "./Widgets/Surveil";
import Ventilator from "./Widgets/Ventilator/Ventilator";
import VentilatorEvents from "./Widgets/Ventilator/VentilatorEvents";
import Vitals from "./Widgets/Vitals/Vitals";
import SnippetReport from "./Widgets/SnippetReport";
import ObSummary from "./Widgets/ObSummary";
import ObSurveil from "./Widgets/ObSurveil";
import TeleReviewMPV from "./Widgets/TeleReview/features/TeleReviewMPV/TeleReviewMPV";
import TeleReviewSPV from "./Widgets/TeleReview/features/TeleReviewSPV/TeleReviewSPV";
import VendorLogin from "./Widgets/VendorLogin";
import IconDemo from "shared/Icon/Demo";

let appStore;

const Router = HashRouter;

class _Routes extends Component {
  routes = [
    {
      path: "/patients/surveil/:listIndex?",
      component: Surveil,
    },
    {
      path: "/patients/ob-surveil/:listIndex?",
      component: ObSurveil,
    },
    {
      path: "/patients/:listIndex/tele-review-mpv",
      component: TeleReviewMPV,
    },
    {
      path: "/patients/:listIndex?",
      component: Patients,
    },
    {
      path: "/patient/:asmpid",
      exact: true,
      component: PatientTiles,
    },
    {
      path: "/patient/:asmpid/overview",
      exact: true,
      component: PatientTiles,
    },
    {
      path: "/patient/:asmpid/ob-summary",
      exact: true,
      component: ObSummary,
    },
    {
      path: "/patient/:asmpid/ventilator(/graphs)?",
      exact: true,
      component: Ventilator,
    },
    {
      path: "/patient/:asmpid/ventilator/events",
      component: VentilatorEvents,
    },
    {
      path: "/patient/:asmpid/labs",
      component: Labs,
    },
    {
      path: "/patient/:asmpid/lab/event/:eventId/order/:orderId/(has-corrections)?",
      component: LabDetail,
    },
    {
      path: "/patient/:asmpid/meds",
      component: Meds,
    },
    {
      path: "/patient/:asmpid/med-detail",
      component: MedDetail,
    },
    {
      path: "/patient/:asmpid/vitals",
      component: Vitals,
    },
    {
      path: "/patient/:asmpid/documents/:parentId?/:documentId?",
      component: Documents,
    },
    {
      path: "/patient/:asmpid/monitor/events",
      component: MonitorEvents,
    },
    {
      path: "/patient/:asmpid/monitor/(tools|snippet)/:timestamp?",
      component: Tools,
    },
    {
      path: "/patient/:asmpid/monitor/create-statement",
      component: CreateStatement,
    },
    {
      path: `/patient/:asmpid/monitor/:historyType(history|event|event-history|live|baseline)?/:timestamp?`,
      component: Monitor,
    },
    {
      path: "/patient/:asmpid/monitor/tele-review/:documentId?",
      component: TeleReviewSPV,
    },
    {
      path: "/patient/:asmpid/ecgs/:ecgId?/(compare)?/:compareId?/(lead)?/:lead?",
      component: ECG,
    },
    {
      path: "/patient/:asmpid/images",
      component: Images,
    },
    {
      path: "/supervisor-report",
      component: SnippetReport,
    },
  ];

  constructor(props) {
    super(props);
    // if sharelink, clear localStorage and authToken
    if (window.location.hash.includes("/api/sharelink")) {
      authStore.authToken = false;
      authStore.username = false;
      authStore.vendorLoginRequired = false;
    }
    appStore = appStore || new AppStore();
    this.state = {
      loggedIn: !!authStore.authToken,
    };
    reaction(
      () => authStore.vendorLoginRequired,
      (vendorLoginRequired) => {
        this.setState({ vendorLoginRequired });
      },
    );
    reaction(
      () => !!authStore.authToken,
      (loggedIn) => {
        this.setState({ loggedIn });
      },
    );
  }

  completeVendorLogin = () => {
    this.setState({ vendorLoginRequired: false });
  };

  render() {
    return (
      <StoreProvider store={appStore}>
        <Provider store={appStore}>
          <Router basename={config.base}>
            <Switch>
              <Route path="/logout" component={Logout} />
              <Route path="/api/sharelink" component={ShareLink} />
              {!IconDemo.isDisabled && (
                <Route path="/icons" component={IconDemo} />
              )}
              <Route
                render={(routeParams) => (
                  <AuthenticatedRoute {...routeParams} parent={this} />
                )}
              />
            </Switch>
          </Router>
        </Provider>
      </StoreProvider>
    );
  }
}
const HistoryContext = React.createContext();
const Routes = observer(_Routes);

export { Routes, HistoryContext };

function Logout() {
  if (authStore.authToken) {
    authStore.deauth();
  }
  // show the login form automatically after 3 seconds
  setTimeout(() => {
    if (config.ENABLE_SITE_SWITCHER && config.SITE_LAUNCHER_URL) {
      window.location = config.SITE_LAUNCHER_URL;
    } else window.location = document.baseURI || "#";
  }, 3000);

  // show the default login background
  return <div className="login"></div>;
}

function AuthenticatedRoute({ location, history, parent }) {
  let { menuOpen, patient, messaging } = appStore;
  return (
    <HistoryContext.Provider value={history}>
      {parent.state.loggedIn ? (
        <div
          {...className({
            "component-root": 1,
            "menu-open": menuOpen,
            "has-patient": patient && location.pathname.match(/^\/patient\//),
          })}
        >
          <Header />
          {parent.state.vendorLoginRequired ? (
            <VendorLogin completeVendorLogin={parent.completeVendorLogin} />
          ) : (
            <div className="app-content-container">
              <Switch location={location}>
                <Route path={["", "/"]} exact={true}>
                  <Redirect to="/patients" />
                </Route>
                {parent.routes.map(({ component: Page, ...route }) => (
                  <Route
                    key={route.path}
                    {...route}
                    render={(props) => {
                      return (
                        <Page {...props} key={props.match?.params?.asmpid} />
                      );
                    }}
                  />
                ))}
                <Redirect to="/patients" />
              </Switch>

              {messaging ? (
                <Route path="/" component={MessagingContainer} />
              ) : null}
            </div>
          )}
        </div>
      ) : (
        <Login />
      )}
    </HistoryContext.Provider>
  );
}
